import { useSelectedWorkareaPricing } from '../../../domain/workareas/workareaPricingQueries'
import { applySelectedInvoicePricing, buildInvoicePricingSelections } from './invoiceRecoveryModel'
import { faCreditCard, faFileInvoiceDollar } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Alert, Button, DatePicker, Flex, Modal, Skeleton, Space } from "antd"
import dayjs from "dayjs"
import { useContext, useEffect, useReducer, useRef } from "react"
import { AuthenticationContext } from "../../../AuthenticationContext"
import { useWorkareaGroupsQuery } from "../../../domain/workareas/groupsQueries"
import { hasValue } from "../../../helpers/nullable"
import { ItemsContext } from "../../Items/ItemsRoot"
import LabeledSwitch from "../../common/LabeledSwitch"
import VSpace from "../../common/VSpace"
import { centsToDollarsDisplay } from "../../displayFormatters"
import InvoiceGroup from "./InvoiceGroup"
import { buildInvoiceRequest, calculateInvoiceTotal, createInvoiceWorkareaGroups, getDefaultDueDate, getDefaultGroupingForJobType, hasAutomaticTaxRateGroups, isCreateBlockedByUnresolvableTaxRate } from "./createInvoiceModel"
import { reducer } from "./createInvoiceReducer"
import { PricingTypes } from "../../../common/constants"
import QuotedPriceCalculator from "./QuotedPriceCalculator"
import { useCompletedWorkareaVisitsQuery, useCreateInvoiceMutation, useInvoiceSummariesForJobQuery, useTransactionsForJobQuery } from "../../../domain/invoicing/invoicingQueries"
import { useResolvedTaxRateQuery } from "../../../domain/invoicing/resolvedTaxRateQueries"
import { InvoicingSettings } from "../../../domain/invoicing/settings/invoicingSettings"
import useFeatureFlag from "../../../hooks/useFeatureFlag"
import SwitchWithDescription from "../../common/SwitchWithDescription"
import { CreateInvoiceResponse } from "../../../domain/invoicing/invoicingApi"

type Props = {
  isOpen: boolean
  onClose: () => void
  invoicingSettings: InvoicingSettings
  onInvoiceCreated: (data: CreateInvoiceResponse) => void
  onInvoiceCreationError: (error: unknown) => void
}

const CreateInvoiceModal = ({ isOpen, onClose, invoicingSettings, onInvoiceCreated, onInvoiceCreationError }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const { jobData, jobItemsQuery, changeSet } = useContext(ItemsContext)
  const workareaGroupsQuery = useWorkareaGroupsQuery(jobData.jobId)
  const completedWorkareaVisitsQuery = useCompletedWorkareaVisitsQuery(jobData.jobId, jobData.workareas.some(x => x.pricingType === PricingTypes.PerVisit))
  const transactionsQuery = useTransactionsForJobQuery(jobData.jobId)
  const invoiceSummariesQuery = useInvoiceSummariesForJobQuery(jobData.jobId)
  const [state, dispatch] = useReducer(reducer, {
    workareaGroups: [],
    enableCreditCardPayments: invoicingSettings.defaultPaymentMethods.creditCard,
    enableACHPayments: invoicingSettings.defaultPaymentMethods.ach,
    showAuthorizePaymentOnFile: invoicingSettings.defaultAuthorizationForPaymentOnFile.showAuthorizePaymentOnFile,
    requireAuthorizePaymentOnFile: invoicingSettings.defaultAuthorizationForPaymentOnFile.requireAuthorizePaymentOnFile,
    includePaymentSummary: invoicingSettings.defaultIncludePaymentSummary,
    dueDate: getDefaultDueDate(invoicingSettings)
  })
  const createInvoiceMutation = useCreateInvoiceMutation(onInvoiceCreated, onInvoiceCreationError)
  const { selections, recoveryGroupIds } = buildInvoicePricingSelections(state, workareaGroupsQuery.data ?? [])
  const selectionPricing = useSelectedWorkareaPricing(jobData.jobId, selections, isOpen && selections.length > 0)
  const isPricingPending = selections.length > 0 && (selectionPricing.isLoading || selectionPricing.isFetching)
  const isPricingError = selections.length > 0 && !isPricingPending && selectionPricing.isError
  const pricedState = applySelectedInvoicePricing(state, selections.length ? selectionPricing.data ?? [] : [])
  const invoiceTotal = calculateInvoiceTotal(pricedState)
  const isModalLoading = workareaGroupsQuery.isLoading || completedWorkareaVisitsQuery.isLoading || transactionsQuery.isLoading || invoiceSummariesQuery.isLoading
  const isAutoTaxFeatureEnabled = useFeatureFlag('automatic-tax-rates')
  const hasAutomaticGroups = isAutoTaxFeatureEnabled
    && invoicingSettings.automaticTaxIsActive
    && hasAutomaticTaxRateGroups(state, invoicingSettings.invoicingSystem)
  const resolvedTaxRateQuery = useResolvedTaxRateQuery(jobData.jobId, hasAutomaticGroups)
  const isBlockedByUnresolvableTaxRate = isCreateBlockedByUnresolvableTaxRate(hasAutomaticGroups, resolvedTaxRateQuery.data)
  const showTaxCodeWarning = (
    currentUser?.isTenantConnectedToQuickBooks === true
    && currentUser?.tenantQuickbooksCompanyCountryCode !== 'US'
    && !hasValue(currentUser?.tenant.defaultQboSalesTaxId)
  )

  const hasInitializedWorkareaGroups = useRef(false)
  useEffect(() => {
    if (hasInitializedWorkareaGroups.current) {
      return
    }
    if (isModalLoading || !workareaGroupsQuery.data || !jobItemsQuery.data) {
      return
    }
    const defaultGrouping = getDefaultGroupingForJobType(invoicingSettings, jobData.jobType)
    const { estimatedItems, actualItems } = jobItemsQuery.data
    const workareaGroups = createInvoiceWorkareaGroups(
      workareaGroupsQuery.data,
      invoiceSummariesQuery.data ?? [],
      transactionsQuery.data ?? [],
      estimatedItems,
      actualItems,
      currentUser?.tenant.defaultQboSalesTaxId,
      currentUser?.tenant.defaultQboExemptSalesTaxId,
      completedWorkareaVisitsQuery.data ?? [],
      defaultGrouping
    )

    dispatch({ type: 'RESET_WORKAREA_GROUPS', workareaGroups })
    hasInitializedWorkareaGroups.current = true
  }, [isModalLoading, workareaGroupsQuery.data, jobItemsQuery.data, invoiceSummariesQuery.data, transactionsQuery.data, completedWorkareaVisitsQuery.data])

  return <Modal
    title={<Space><FontAwesomeIcon icon={faFileInvoiceDollar} />Create New Invoice</Space>}
    open={isOpen}
    onCancel={onClose}
    onOk={() => createInvoiceMutation.mutate(buildInvoiceRequest(pricedState, jobData.jobId, changeSet.updatedItems, invoicingSettings.invoicingSystem))}
    okText="Create Invoice"
    width={800}
    okButtonProps={{ disabled: isPricingPending || isPricingError || createInvoiceMutation.isLoading || invoiceTotal <= 0 || showTaxCodeWarning || isBlockedByUnresolvableTaxRate, loading: createInvoiceMutation.isLoading }}
    cancelButtonProps={{ disabled: createInvoiceMutation.isLoading }}
    maskClosable={false}
  >
    {isModalLoading && <Skeleton active />}
    {!isModalLoading && <VSpace>
      {isPricingError && <Alert type="error" message="Unable to calculate the selected work area pricing." action={<Button onClick={() => selectionPricing.refetch()}>Retry</Button>} />}
      {showTaxCodeWarning && <VSpace>
        <Alert showIcon type="error" message={<>You must select default rates to apply to taxable and non-taxable workareas in <a href="/#!/mycompany" target="_blank">My Company settings</a> before you can invoice.</>} />
      </VSpace>}
      {isBlockedByUnresolvableTaxRate && <Alert
        showIcon
        type="warning"
        message="Location-based tax rate unavailable"
        description="We couldn't determine a tax rate from this job's service location. Choose a tax rate for each group set to Calculate Automatically, or fix the job's address."
      />}

      <QuotedPriceCalculator
          jobId={jobData.jobId}
          workareaGroups={state.workareaGroups}
          dispatch={dispatch}
      />

      Select the workareas to include in the invoice.
      {pricedState.workareaGroups.map(g => <InvoiceGroup
        key={g.id}
        group={g}
        isPricingPending={isPricingPending && recoveryGroupIds.includes(g.id)}
        isPricingError={isPricingError && recoveryGroupIds.includes(g.id)}
        dispatch={dispatch}
        isDownPayment={false}
        invoicingSystem={invoicingSettings.invoicingSystem}
        jobId={jobData.jobId}
      />)}

      <Flex justify="space-between" style={{ marginTop: '10px' }}>
        <VSpace>
          Due On
          <DatePicker
            value={dayjs(state.dueDate)}
            onChange={(value) => dispatch({ type: 'DUE_DATE_CHANGED', value: value?.toISOString() ?? getDefaultDueDate(invoicingSettings) })}
          />
        </VSpace>
        <VSpace>
          Online Payments
          <LabeledSwitch
            value={state.enableCreditCardPayments}
            label={<span>Enable Card Payments <FontAwesomeIcon icon={faCreditCard} /></span>}
            onChange={() => dispatch({ type: 'CREDIT_CARD_ENABLED_CHANGED', value: !state.enableCreditCardPayments })}
          />
          <LabeledSwitch
            value={state.enableACHPayments}
            label="Enable Bank Transfers (ACH)"
            onChange={() => dispatch({ type: 'ACH_ENABLED_CHANGED', value: !state.enableACHPayments })}
          />
        </VSpace>
      </Flex>
      {invoicingSettings.invoicingSystem === 'synkedUp' && <VSpace size="large">
        <SwitchWithDescription
          checked={state.includePaymentSummary}
          label="Include Payment Summary"
          description="Show the job total, payments made, and remaining balance on the customer's invoice."
          onClick={() => dispatch({ type: 'INCLUDE_PAYMENT_SUMMARY_CHANGED', value: !state.includePaymentSummary })}
        />
        <SwitchWithDescription
          checked={state.showAuthorizePaymentOnFile}
          label="Request Payment Method on File"
          description="Display the option for the customer to authorize saving their payment method for future use."
          onClick={() => dispatch({ type: 'SHOW_AUTHORIZE_PAYMENT_ON_FILE_CHANGED', value: !state.showAuthorizePaymentOnFile })}
        />
        {state.showAuthorizePaymentOnFile && <div style={{marginLeft: 50, borderLeft: '1px solid #00000026', paddingLeft: 12}}>
          <SwitchWithDescription
            checked={state.requireAuthorizePaymentOnFile}
            label="Require Payment Method on File"
            description="Make it required for the customer to authorize saving their payment method for future use."
            onClick={() => dispatch({ type: 'REQUIRE_AUTHORIZE_PAYMENT_ON_FILE_CHANGED', value: !state.requireAuthorizePaymentOnFile })}
          />
        </div>}
      </VSpace>}
      <Flex justify="end" style={{ fontSize: '1.5em' }}>
        Total: {isPricingPending ? 'Calculating…' : isPricingError ? 'Unavailable' : centsToDollarsDisplay(invoiceTotal)}
      </Flex>
    </VSpace>}
  </Modal>
}

export default CreateInvoiceModal
